import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Badge,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Text,
  TextInput,
  Title,
} from '@tremor/react';
import { RiDownloadLine, RiSearchLine, RiRefreshLine } from '@remixicon/react';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import { reportService } from '../../services/report';

type SavedReport = {
  system?: { id?: string; company?: string; actor?: string };
  details?: { types?: string[]; filter?: any; payload?: Record<string, any> };
  audit?: { created_at?: string };
};

function formatFilter(filter: any) {
  if (!filter) return '—';
  const type = filter.type || 'unknown_filter';
  const id = filter.id || 'unknown';
  return `${type}:${id}`;
}

async function exportSavedReport(report: SavedReport, format: 'xlsx' | 'csv') {
  const payload = report?.details?.payload || {};
  const workbook = new ExcelJS.Workbook();

  const entries = Object.entries(payload);
  if (entries.length === 0) {
    throw new Error('No payload found for this report.');
  }

  for (const [key, data] of entries) {
    const sheetData = Array.isArray(data)
      ? data
      : data && typeof data === 'object'
        ? [data]
        : [{ value: data }];

    const worksheet = workbook.addWorksheet(String(key).slice(0, 31));
    if (sheetData.length > 0) {
      worksheet.columns = Object.keys(sheetData[0]).map((col) => ({
        header: col,
        key: col,
      }));
      sheetData.forEach((row: any) => worksheet.addRow(row));
    }
  }

  let buffer: ArrayBuffer;
  if (format === 'csv') {
    // For CSV, export only the first worksheet
    const csvWorkbook = new ExcelJS.Workbook();
    const srcSheet = workbook.worksheets[0];
    const csvSheet = csvWorkbook.addWorksheet('Sheet1');
    srcSheet.eachRow((row, rowNumber) => {
      csvSheet.getRow(rowNumber).values = row.values as any;
    });
    buffer = (await csvWorkbook.csv.writeBuffer()) as ArrayBuffer;
  } else {
    buffer = (await workbook.xlsx.writeBuffer()) as ArrayBuffer;
  }

  const blob = new Blob([buffer], {
    type:
      format === 'csv'
        ? 'text/csv'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const id = report?.system?.id || 'report';
  const created = report?.audit?.created_at
    ? new Date(report.audit.created_at).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  saveAs(blob, `saved-report-${id}-${created}.${format}`);
}

export default function SavedReports() {
  const [search, setSearch] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);

  const {
    data: response,
    isLoading,
    isError,
    refetch,
  } = useQuery('savedReports', reportService.getSavedReports);

  const reports: SavedReport[] = useMemo(
    () => response?.payload || [],
    [response?.payload],
  );

  const filtered = useMemo(() => {
    if (!search) return reports;
    const q = search.toLowerCase();
    return reports.filter((r) => {
      const types = (r.details?.types || []).join(',').toLowerCase();
      const filter = formatFilter(r.details?.filter).toLowerCase();
      const created = r.audit?.created_at
        ? new Date(r.audit.created_at).toLocaleString().toLowerCase()
        : '';
      return (
        types.includes(q) ||
        filter.includes(q) ||
        created.includes(q) ||
        String(r.system?.id || '').toLowerCase().includes(q)
      );
    });
  }, [reports, search]);

  const handleExport = async (report: SavedReport, format: 'xlsx' | 'csv') => {
    setExportError(null);
    const id = report?.system?.id || null;
    setExportingId(id);
    try {
      await exportSavedReport(report, format);
    } catch (err: any) {
      const message =
        typeof err?.message === 'string'
          ? err.message
          : 'Failed to export report.';
      setExportError(message);
    } finally {
      setExportingId(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Title>Saved reports</Title>
          <Text className="mt-1">
            Previously generated report snapshots for your workspace.
          </Text>
        </div>
        <Button
          variant="secondary"
          icon={RiRefreshLine}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </div>

      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TextInput
            icon={RiSearchLine}
            placeholder="Search by type, filter, date, id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Text className="text-sm text-gray-500">
            {filtered.length} report{filtered.length === 1 ? '' : 's'}
          </Text>
        </div>

        {exportError && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2">
            <Text className="text-sm text-red-700">{exportError}</Text>
          </div>
        )}

        {isLoading && (
          <div className="py-8 text-center text-sm text-gray-500">
            Loading saved reports…
          </div>
        )}

        {isError && (
          <div className="py-8 text-center text-sm text-red-600">
            Failed to load saved reports.
          </div>
        )}

        {!isLoading && !isError && (
          <>
            {filtered.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">
                No saved reports found.
              </div>
            ) : (
              <Table className="mt-6">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Created</TableHeaderCell>
                    <TableHeaderCell>Types</TableHeaderCell>
                    <TableHeaderCell>Filter</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((r, idx) => (
                    <TableRow
                      key={r.system?.id || r.audit?.created_at || String(idx)}
                    >
                      <TableCell>
                        {r.audit?.created_at
                          ? new Date(r.audit.created_at).toLocaleString()
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(r.details?.types || []).length ? (
                            (r.details?.types || []).slice(0, 4).map((t) => (
                              <Badge key={t} color="gray">
                                {t}
                              </Badge>
                            ))
                          ) : (
                            <Text className="text-sm text-gray-500">—</Text>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatFilter(r.details?.filter)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="xs"
                            variant="secondary"
                            icon={RiDownloadLine}
                            loading={exportingId === (r.system?.id || null)}
                            onClick={() => handleExport(r, 'xlsx')}
                          >
                            XLSX
                          </Button>
                          <Button
                            size="xs"
                            variant="secondary"
                            icon={RiDownloadLine}
                            loading={exportingId === (r.system?.id || null)}
                            onClick={() => handleExport(r, 'csv')}
                          >
                            CSV
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

